namespace Solovis.Reports {
  export enum FeaturedLinkType {
    Shared = 0,
    ReportSetting = 1,
    Favorite = 2
  }

  export interface ReportDataContext {
    dataEnvironmentId?: number;
    subdomain?: string;
    perspectiveCode?: string;
  }

  export interface RunReportOptions {
    doNotUseCache?: boolean;
  }

  export interface ReportControlOptions extends Solovis.UI.BaseControlOptions {
    dataContext?: ReportDataContext;

    portalServices?: Solovis.Portal.IPortalServices;

    parentReportControl?: ReportControl;

    report?: any;
    reportPath?: string;
    reportCode?: string;
    reportAlias?: string;

    widgetCode?: string;
    widgetTitle?: string;

    reportResourceId?: number;

    printMode?: any;

    reportConfigSectionClass?: string;

    persistedSettings?: any;
    defaultPersistedSettings?: any;

    parameters?: any;

    hiddenParameters?: string[];

    autoRun?: boolean;
    autoRunOnParameterChange?: boolean;
    autoRunCondition?: any;
    hideRunButton?: boolean;
    hideReportOptionsButton?: boolean;
    hideConfigSection?: boolean;
    collapseConfigSection?: boolean;
    fullWidth?: boolean;
    showConfigSectionInline?: boolean;
    contentHeight?: any;

    restrictedParameterOptions?: any[];

    autoSizeSpaceAvailable?: boolean;

    cachePolicy?: any;
    initialValueOverrides?: any;

    autoRunReportOptions?: RunReportOptions;

    isFavorite?: boolean;
    featuredLinkCode?: string;
  }

  export class ReportControl extends Solovis.UI.BaseControl {

    public static _log: boolean = false;

    private _reportOptions: ReportControlOptions;

    private _dataContext: ReportDataContext;

    public parentReportControl: ReportControl;
    public report: any;
    public reportPath: string;
    public reportCode: string;
    public reportAlias: string;
    public reportResourceId: number;
    public printMode: any;

    public widgetCode: string;
    public widgetTitle: string;

    public _reportConfigSectionClass;
    public reportPartList;
    public _rootReportPart;
    public elementNameToReportPartMap;
    public _persistedSettings;

    public _runCount;
    private _lastRunStartTime: number;
    private _lastDataLoadElapsedMs: number;

    public _initReportError: any;

    public _waitManager: Solovis.Reports.ReportControlHelpers.WaitManager;
    public _parameterSyncManager: Solovis.Reports.ReportControlHelpers.SubReportParameterSyncManager;

    public _reportName: string;
    public reportLoadError;
    public titleSettings: {
      defaultTitle?: string;
      useParameter?: {
        prefix?: string;
        name: string;
        suffix?: string;
      },
      dynamic?: {
        ref?: {
          elementName: string;
          dataSourceName: string;
        }
      },
    };
    public _loadStatus;
    public shouldAutoRunOnParameterChange: boolean;
    public _uiOptions;
    public _parameterSet: Solovis.Reports.ParameterSet;
    public _restrictedParameterOptions;
    public _autoSizeSpaceAvailable: boolean;
    public _windowResizeForAutoSizeHandler;
    public _cachePolicy;
    public _spaceAvailable;

    public _configControlDiv: JQuery;
    public _configControlDivVisible: boolean;
    public _configControlHost: Solovis.UI.ControlHost;
    public _configControl: Solovis.Reports.ReportConfigControl;

    public _showReportOptionsCallback;
    public _showRelatedLinksCallback;
    public _printModeDiv: JQuery;
    public errorDiv: JQuery;
    public _cacheSummaryHeaderDiv: JQuery;
    public _dataPackSummaryHeaderDiv: JQuery;
    public _partsContainer: JQuery;
    public isFavorite: boolean;
    public featuredLinkCode: string;

    /*
     * _hiddenOverrideElementMap tracks list of elements that are hidden regardless of the normal report logic.
     */
    private _partHiddenOverrideMap: any;

    public _debouncedRegularRunner: any;
    public _lastParameterValidationState: any;
    public _configWindowDialog: Solovis.UI.Dialog;

    private _runReportOptions: RunReportOptions;

    private newControlsSupported = false;

    constructor(options: ReportControlOptions) {
      super(_.assign((options || {}), {
        renderMode: (Solovis.UX.Utils.areNewControlsSupported() ? 'modern' : 'classic') as Solovis.UI.BaseControlOptions['renderMode']
      }));

      this._reportOptions = options;

      var self = this;

      this._dataContext = options.dataContext;

      this.parentReportControl = options.parentReportControl ? options.parentReportControl : null;

      this.report = options.report ? options.report : null;
      this.reportPath = options.reportPath ? options.reportPath : null;
      this.reportCode = options.reportCode ? options.reportCode : null;
      this.reportAlias = options.reportAlias ? options.reportAlias : null;
      this.reportResourceId = options.reportResourceId ? options.reportResourceId : null;
      this.isFavorite = options.isFavorite ? options.isFavorite : false;
      this.featuredLinkCode = options.featuredLinkCode ? options.featuredLinkCode : null;

      this.widgetCode = options.widgetCode ? options.widgetCode : null;
      this.widgetTitle = options.widgetTitle ? options.widgetTitle : null;

      this.printMode = options.printMode ? options.printMode : false;

      this._reportConfigSectionClass = options.reportConfigSectionClass ? options.reportConfigSectionClass : null;

      // Set up tracking variables for report parts.
      this.reportPartList = [];
      this._rootReportPart = null;
      this.elementNameToReportPartMap = {};

      this._persistedSettings = options.persistedSettings ? options.persistedSettings : null;

      this._waitManager = new Solovis.Reports.ReportControlHelpers.WaitManager({
        reportControl: this
      });

      this._partHiddenOverrideMap = {};

      // Number of times _runReport has been called.
      this._runCount = 0;
    }


    public async doInitAsync(): Promise<void> {
      if (!this.report && (this.reportPath || this.reportCode || this.reportAlias)) {
        var reportRef: any = {};
        if (this.reportCode) {
          reportRef.code = this.reportCode;
        }
        else if (this.reportPath) {
          reportRef.path = this.reportPath;
        }
        else if (this.reportAlias) {
          reportRef.alias = this.reportAlias;
        }

        this._waitManager.waitingHasBegun();

        let getReportResponse;
        try {
          getReportResponse = await this.portalServices().reportDataLoader().getReportDefinitionAsync(reportRef);
        }
        finally {
          this._waitManager.waitingHasEnded();
        }

        let getReportResult = getReportResponse.result;

        var reportDefinition = getReportResult.report;
        // The report name can be used as a backup title if none is explicitly defined in the report definition.
        this._reportName = getReportResult.name;

        this.reportCode = getReportResult.code;
        this.reportPath = getReportResult.path;
        this.reportResourceId = getReportResult.resourceId;
        this.report = reportDefinition;
      }

      if (this.report.parameters instanceof Promise) {
        const params = await this.report.parameters;
        this.report.parameters = params;
      }

      this._tryInitReport();
    }

    public _tryInitReport() {
      try {
        this._initReport();
      }
      catch (ex) {
        console.log("ERROR: Unable to initialize report.");
        console.log(ex);
        throw ex;
      }
    }

    public _initReport() {
      var self = this;

      var options: any = this.options;

      var getTopLevelOrReportOption = function (optionName, defaultValue) {
        var optionValue = defaultValue;
        if (typeof options[optionName] != "undefined") {
          return options[optionName];
        }

        if (!self.report) {
          // Logging rare error that should not occur in initializing report. Following if test will cause exception.
          console.log("Unexpected error: report definition not present");
        }

        if (typeof self.report[optionName] != "undefined") {
          return self.report[optionName];
        }

        return defaultValue;
      };

      this.titleSettings = getTopLevelOrReportOption("title", {});

      // autoRun may be specified in top-level options or on report definition.
      var shouldAutoRun = getTopLevelOrReportOption("autoRun", false);

      if (this._persistedSettings && this._persistedSettings.hasRun) {
        // If the report had previously been run, it needs to be run when it is restored.
        shouldAutoRun = true;
      }

      var autoRunCondition = getTopLevelOrReportOption("autoRunCondition", false);

      this._loadStatus = {
        allParametersInitialized: false,
        partContainersCreated: false,
        shouldAutoRun: shouldAutoRun,
        autoRunCondition: autoRunCondition
      };

      // autoRunOnParameterChange may be specified in top-level options or on report definition.    
      this.shouldAutoRunOnParameterChange = getTopLevelOrReportOption("autoRunOnParameterChange", false);
      if (this.shouldAutoRunOnParameterChange) {
        // If we should auto-run on parameter change we should also always autoRun at load time.
        this._loadStatus.shouldAutoRun = this.shouldAutoRunOnParameterChange;
      }

      // load ui options 
      var hideRunButton = getTopLevelOrReportOption("hideRunButton", false);
      var hideReportOptionsButton = getTopLevelOrReportOption("hideReportOptionsButton", false);
      var hideConfigSection = getTopLevelOrReportOption("hideConfigSection", false);
      var collapseConfigSection = getTopLevelOrReportOption("collapseConfigSection", false);
      var fullWidth = getTopLevelOrReportOption("fullWidth", true);
      var showConfigSectionInline = getTopLevelOrReportOption("showConfigSectionInline", null);
      var contentHeight = getTopLevelOrReportOption("contentHeight", null);

      this._uiOptions = {
        hideRunButton: hideRunButton,
        hideReportOptionsButton: hideReportOptionsButton,
        hideConfigSection: hideConfigSection,
        collapseConfigSection: collapseConfigSection,
        fullWidth: fullWidth,
        showConfigSectionInline: showConfigSectionInline,
        contentHeight: contentHeight
      };

      // See if we need to use default persisted settings (for tailored reports)
      if (!this._persistedSettings) {
        this._persistedSettings = getTopLevelOrReportOption("defaultPersistedSettings", null);
      }

      // Set up the parameter set.
      var parameters = this._getParametersToUse();
      this._parameterSet = new Solovis.Reports.ParameterSet({ parameters: parameters, portalServices: this.portalServices() });

      // Determine if we have restricted parameter options.
      this._restrictedParameterOptions = {};
      if (this.report.restrictedParameterOptions) {
        for (var paramName in self.report.restrictedParameterOptions) {
          this._restrictedParameterOptions[paramName] = this.report.restrictedParameterOptions[paramName];
        }
      }
      if (options.restrictedParameterOptions) {
        // Restricted parameter options on options passed directly into ReportControl take precedence.
        for (var paramName in options.restrictedParameterOptions) {
          this._restrictedParameterOptions[paramName] = options.restrictedParameterOptions[paramName];
        }
      }

      // See if we need to auto-size vertical space.
      this._autoSizeSpaceAvailable = getTopLevelOrReportOption("autoSizeSpaceAvailable", false);
      if (this._autoSizeSpaceAvailable) {
        if (this.isTopLevelReportControl()) {
          this._windowResizeForAutoSizeHandler = function () { self._updateSpaceAvailable(); };
          $(window).bind("resize", this._windowResizeForAutoSizeHandler);
        }
      }

      // Check for report policy.
      this._cachePolicy = getTopLevelOrReportOption("cachePolicy", null);

      // Restore previously saved list of part hidden overrides.
      if (this._persistedSettings && this._persistedSettings.__partHiddenOverrides) {
        for (let partElementName of this._persistedSettings.__partHiddenOverrides) {
          this._partHiddenOverrideMap[partElementName] = true;
        }
      }
    };


    /**
     * Retrieve a previously preserved parameter value.
     * @param {string}paramName - The name of the parameter.
     * @param {string}from - Should always be "session" for now. "local" may be supported later.
     * @param {string}scope - "report" indicates same paramName for this reportCode.
     */
    public _getPreservedParameterValue(paramName, from, scope) {
      try {
        if (window.sessionStorage) {
          var reportCode = this.reportCode;
          var key = "preservedParamValue_" + reportCode + "_" + paramName;
          var valueJson = window.sessionStorage.getItem(key);
          if (valueJson) {
            var value = JSON.parse(valueJson);
            return value;
          }
        }
      }
      catch (ex) {
        console.log("ERROR: Unable to retrieve preserved value.");
        console.log(ex);
      }

      return null;
    }

    /**
     * Retrieve a previously preserved parameter value.
     * @param {string}paramName - The name of the parameter.
     * @param {string}value - The value of the parameter.
     * @param {string}from - Should always be "session" for now. "local" may be supported later.
     * @param {string}scope - "report" indicates same paramName for this reportCode.
     */
    public _setPreservedParameterValue(paramName, value, from, scope) {
      try {
        if (window.sessionStorage) {
          var reportCode = this.reportCode;
          var key = "preservedParamValue_" + reportCode + "_" + paramName;
          var valueJson = JSON.stringify(value);
          window.sessionStorage.setItem(key, valueJson);
        }
      }
      catch (ex) {
        console.log("ERROR: Unable to preserve value.");
        console.log(ex);
      }
    }

    public _getParametersToUse() {
      var self = this;
      var reportCode = this.reportCode;
      var parameters = this.report.parameters ? Solovis.deepCopy(this.report.parameters) : [];
      var options: any = this.options;

      if (options.hiddenParameters) {
        var hiddenParameterMap: any = {};
        $(options.hiddenParameters).each(function (i, hiddenParamName: any) {
          hiddenParameterMap[hiddenParamName] = true;
        });

        $(parameters).each(function (i, param: any) {
          if (hiddenParameterMap[param.name]) {
            param.hidden = true;
          }
        });
      }

      // See if we need to preserve a previous value from the user's session.        
      $(parameters).each(function (i, param: any) {
        if (param.preserveValue) {
          var preserveValue = param.preserveValue;
          if (preserveValue.from == "session" && preserveValue.scope == "report") {
            var preservedParamValue = self._getPreservedParameterValue(param.name, preserveValue.from, preserveValue.scope);
            if (Solovis.isNotNull(preservedParamValue)) {
              param.initialValue = preservedParamValue;
            }
          }
          else {
            console.log("ERROR: parameter preserveValue must be for session/report currently: " + param.name);
          }
        }
      });

      // See if we need to override initial parameter values.
      var initialValueOverrides = null;
      if (options.initialValueOverrides) {
        initialValueOverrides = options.initialValueOverrides;
      }
      else if (this.report.initialValueOverrides) {
        initialValueOverrides = this.report.initialValueOverrides;
      }

      if (initialValueOverrides) {
        $(parameters).each(function (i, param: any) {
          if (typeof initialValueOverrides[param.name] != "undefined") {
            param.initialValue = Solovis.deepCopy(initialValueOverrides[param.name]);
          }
        });
      }

      if (options.parameters) {
        var optionsParams = options.parameters;
        // Parameters supplied at top-level can override initialValue on parameters.
        $(parameters).each(function (i, param: any) {
          if (typeof optionsParams[param.name] != "undefined") {
            param.initialValue = Solovis.deepCopy(optionsParams[param.name]);
          }
        });
      }

      return parameters;
    }



    public portalServices(): Solovis.Portal.IPortalServices {
      if (this._reportOptions.portalServices) {
        return this._reportOptions.portalServices;
      }
      else if (this.parentReportControl) {
        return this.parentReportControl.portalServices();
      }
      else {
        return new Solovis.Portal.SitePortalServices();
      }
    }


    public runCount() {
      return this._runCount;
    }

    public isRunning() {
      if (this._lastRunStartTime != null && this._runCount > 0) {
        return true;
      }
    }

    public doRender() {
      if (this.options.renderMode === 'modern') {
        this.doRenderNew();
      } else {
        this.doRenderClassic();
      }
    }

    public doRenderClassic() {
      var self = this;
      this._configControlDiv = $("<div>");

      this.container.css("position", "relative");

      if (this._spaceAvailable) {
        // This will size the container appropriately if needed at initial render time.
        this.setSpaceAvailable(this._spaceAvailable.width, this._spaceAvailable.height);
      }

      if (this.isTopLevelReportControl()) {
        if (!this._uiOptions.fullWidth) {
          this.container.addClass("report-standard-width");
        }
      }

      if (this.reportLoadError) {
        $("<div>").text(this.reportLoadError.message).appendTo(this.container);
      }

      if (this._initReportError) {
        $("<div>").text(this._initReportError).appendTo(this.container);
        return;
      }

      // Initialize config control        
      if (!this._uiOptions.hideConfigSection) {
        // Show config link.                    
        var showConfigLink = $("<a>")
          .html("<span class='fa fa-gear'></span>")
          .attr("title", "Report Options")
          .on("click contextmenu", function (e) {
            e.preventDefault();
            self._showReportOptionsMenu(showConfigLink);
            return false;
          });

        // showConfigSectionInline if it is explicitly configured or it is not configured and this is a top-level control.
        var showConfigSectionInline = Solovis.isNotNull(this._uiOptions.showConfigSectionInline) ?
          this._uiOptions.showConfigSectionInline
          :
          this.isTopLevelReportControl();

        if (showConfigSectionInline) {
          this._configControlDiv.addClass("report-config-section").addClass("no-print").appendTo(this.container);
          if (this._reportConfigSectionClass) {
            this._configControlDiv.addClass(this._reportConfigSectionClass);
          }

          if (this._uiOptions.collapseConfigSection) {
            this._configControlDiv.css("display", "none");
            this._configControlDivVisible = false;
          }
          else {
            this._configControlDivVisible = true;
          }

          this._configControlHost = new Solovis.UI.ControlHost({ container: this._configControlDiv });
          this._configControl = new Solovis.Reports.ReportConfigControl({
            reportControl: this,
            uiOptions: this._uiOptions,
          });
          this._configControlHost.setCurrentControl(this._configControl);

          this._showReportOptionsCallback = function () {
            self._configControlDiv.toggle();
          };
        }
        else {
          this._showReportOptionsCallback = function () {
            self._showConfigSectionInPopup();
          };
        }

        if (!this._uiOptions.hideReportOptionsButton) {
          if (this.isTopLevelReportControl()) {
            var actionBarRegion = Solovis.Site.ActionBar.getOrCreateRegion('ReportControl');
            if (!Solovis.UX.Utils.isUserEligibleForNewReportHeader()) {
              actionBarRegion.setHtml(showConfigLink);
            }
          }
          else {
            // Stack the gear above the floating column header so large tables in nested report stacks can't intercept its clicks.
            // Must stay higher than the z-index set in DataTableHelpers.FloatingColumnHeader.ts.
            var showConfigDiv = $("<div>").addClass("no-print").css("float", "right")
              .css("position", "relative")
              .css("z-index", 2)
              .appendTo(this.container);
            showConfigDiv.append(showConfigLink);
          }

          // Add drag handler for report settings (allows report to be dragged to Custom Panels/etc with its current state).
          Solovis.Site.DragDropManager.setupSolovisReportControlDrag(showConfigLink, self);
        }
      }


      $(this._parameterSet).bind("allParametersReady", function () {
        try {
          if (ReportControl._log) {
            console.log("ReportControl: allParametersReady");
          }

          self._loadStatus.allParametersInitialized = true;
          self._checkForAutoRun();

          // NOTE: Changing parameters can change the space available for the control, we attempt to resize the control
          //   and trigger a window resize, so that our charts will resize themselves as appropriate. We wouldn't need
          //   this workaround if there were a reliable way to get a DOM event for a DOM element changing size.
          if (self._autoSizeSpaceAvailable) {
            if (self.isTopLevelReportControl()) {
              self._updateSpaceAvailable();
              $(window).trigger("resize");
            }
          }
        }
        catch (ex) {
          Solovis.log(ex);
        }
      });

      $(this._parameterSet).bind("parameterValueChanged", function () {
        try {
          if (ReportControl._log) {
            console.log("ReportControl: parameterValueChanged");
          }

          self._checkForAutoRunOnParameterValueChange();

          // We also need to check if any of the parameters need to be persisted.                
          $(self._parameterSet.parameters).each(function (i, param: any) {
            if (param.preserveValue) {
              var paramValue = self._parameterSet.getParameterValue(param.name);
              self._setPreservedParameterValue(param.name, paramValue, param.preserveValue.from, param.preserveValue.scope);
            }
          });

          // NOTE: Changing parameters can change the space available for the control, we attempt to resize the control
          //   and trigger a window resize, so that our charts will resize themselves as appropriate. We wouldn't need
          //   this workaround if there were a reliable way to get a DOM event for a DOM element changing size.
          if (self._autoSizeSpaceAvailable) {
            if (self.isTopLevelReportControl()) {
              self._updateSpaceAvailable();
              $(window).trigger("resize");
            }
          }
        }
        catch (ex) {
          Solovis.log(ex);
        }
      });

      if (this._parameterSet.areAllParametersReady) {
        // There are no parameters to initialized, so they are already all loaded.
        this._loadStatus.allParametersInitialized = true;

        if (ReportControl._log) {
          console.log("ReportControl: allParametersReady by reason of no initialization needed");
        }
      }

      // Print mode indicator.
      if (this.isTopLevelReportControl()) {
        this._printModeDiv = $("<div>").addClass("print-mode-indicator no-print").css("display", "none").appendTo(this.container);
        this._printModeDiv.text("Print Mode");
        if (this.printMode) {
          this._printModeDiv.css("display", "block");
        }
      }

      // Error div.
      this.errorDiv = $("<div>").addClass("no-print").addClass("report-control-error-section").css("display", "none").appendTo(this.container);

      if (this._cachePolicy && this._cachePolicy.includeCacheSummaryHeader) {
        this._cacheSummaryHeaderDiv = $("<div>").addClass("no-print").addClass("report-control-cache-summary").css("display", "none").appendTo(this.container);
      }

      this._dataPackSummaryHeaderDiv = $("<div>").addClass("no-print").addClass("report-control-pack-summary").css("display", "none").appendTo(this.container);

      this._partsContainer = $("<div>").appendTo(this.container);
      if (this._uiOptions.contentHeight) {
        this._partsContainer.height(this._uiOptions.contentHeight);
      }

      // Create report parts.
      if (this.report.root) {
        this._rootReportPart = this._createPartContainer({ parentElement: this._partsContainer, partDefinition: this.report.root, isRoot: true });
      }

      this._loadStatus.partContainersCreated = true;

      // Setup parameter sync manager.            
      this._parameterSyncManager = new Solovis.Reports.ReportControlHelpers.SubReportParameterSyncManager({
        reportControl: this
      });

      this._checkForAutoRun();

      this._updateSpaceAvailable();
    }

    public doRenderNew() {
      $('.header-title-bar-container').hide();
      var self = this;

      this.container.css("position", "");

      this._showRelatedLinksCallback = function () {
        self._showRelatedLinks();
      }

      if (this._spaceAvailable) {
        // This will size the container appropriately if needed at initial render time.
        this.setSpaceAvailable(this._spaceAvailable.width, this._spaceAvailable.height);
      }

      if (this.isTopLevelReportControl()) {
        if (!this._uiOptions.fullWidth) {
          this.container.addClass("report-standard-width");
        }
      }

      if (this.reportLoadError) {
        $("<div>").text(this.reportLoadError.message).appendTo(this.container);
      }

      if (this._initReportError) {
        $("<div>").text(this._initReportError).appendTo(this.container);
        return;
      }

      // Initialize config control        
      if (!this._uiOptions.hideConfigSection) {
        // Show config link.                    
        var showConfigLink = $("<a>")
          .html("<span class='fa fa-gear'></span>")
          .attr("title", "Report Options")
          .on("click contextmenu", function (e) {
            e.preventDefault();
            self._showReportOptionsMenu(showConfigLink);
            return false;
          });

        // showConfigSectionInline if it is explicitly configured or it is not configured and this is a top-level control.
        var showConfigSectionInline = Solovis.isNotNull(this._uiOptions.showConfigSectionInline) ?
          this._uiOptions.showConfigSectionInline
          :
          this.isTopLevelReportControl();

        if (showConfigSectionInline) {
          this._configControlDiv = $("<div>").appendTo(this.container);
          const pageViewModel = (window as any).PageViewModel;
          const reportId = this.isTopLevelReportControl() ? this.reportCode : this.parentReportControl.reportCode;
          const configContainerId = this.isTopLevelReportControl() ? `${reportId}-basic` : `${reportId}-advanced`;
          // use the top level control to render the controls
          if (this.isTopLevelReportControl()) {
            this._configControlDiv.addClass("paux");
            const target = self._configControlDiv[0];
            let hideChart = false;
            let hideTable = false;
            let disableChart = false;
            const reportStackJson = pageViewModel.getReportStack();
            const isReportStack = reportStackJson !== "{}";
            const elementName = this.report.root.children[0].elementName;
            if (isReportStack) {
              const reportStack = JSON.parse(reportStackJson);
              //grab subReport from persisted settings if exists
              const subReport =
                //if there is persisted settings from previous run use those else use report stack settings
                this._persistedSettings && this._persistedSettings[elementName] && this._persistedSettings[elementName].subReport
                  ? this._persistedSettings[elementName].subReport
                  : reportStack.stackRoot.children[0].reportSettings.persistedSettings;
              if (subReport) {
                const chartKey = Object.keys(subReport).filter(key => key.indexOf("Table") !== -1)[0];
                if (chartKey) {
                  const chart = subReport[chartKey].chart;
                  hideTable = chart.hideTable || false;
                  hideChart = chart.hideChart || false;
                  // if there isn't any chart information
                  if (Object.keys(chart).length === 0) {
                    hideChart = true;
                    disableChart = true;
                  }
                }
              }
            } else {
              hideChart = true;
              disableChart = true;
            }

            const reportOptions = {
              id: reportId,
              title: document.title,
              tabs: isReportStack ? ["Basic", "Advanced"] : undefined,
              onFavorite: () => { Solovis.Portal.FeaturedLinks.showSavePageAsFavorite(); },
              onMountComplete: () => {
                setTimeout(() => {
                  this._setupConfigControls(reportId, configContainerId, 'modern');
                });
              },
              onShare: () => { Solovis.Portal.FeaturedLinks.showShareFeaturedLink(); },
              onSave: Solovis.UX.Utils.isDashboard() ? undefined : (item) => {
                switch (item.value) {
                  case "save":
                    Solovis.Portal.FeaturedLinks.SaveReportSettingAsFeatureLink(pageViewModel.getReportOptionsJson(), pageViewModel.reportCode, pageViewModel.reportAlias);
                    break;
                  case "saveEveryone":
                    Solovis.Portal.FeaturedLinks.showSaveReport(false);
                    break;
                  case "saveSelf":
                    Solovis.Portal.FeaturedLinks.showSaveReport(true);
                    break;
                }
              },
              extras: Solovis.UX.Utils.isDashboard() ? undefined : [
                {
                  label: "Show Chart",
                  checkedListener: "@solovis/report/chart-shown",
                  unCheckedListener: "@solovis/report/chart-hidden",
                  enableListener: "@solovis/report/chart-created",
                  disableListener: "@solovis/report/chart-removed",
                  defaultActive: !hideChart,
                  disabled: disableChart,
                  onChange: (visible: boolean) => {
                    const action = visible ? "show" : "hide";
                    Solovis.UX.Utils.emit(`@solovis/report/${action}-chart`);
                  },
                },
                {
                  label: "Show Table",
                  checkedListener: "@solovis/report/table-shown",
                  unCheckedListener: "@solovis/report/table-hidden",
                  enableListener: "@solovis/report/table-created",
                  disableListener: "@solovis/report/table-removed",
                  defaultActive: !hideTable,
                  onChange: (visible: boolean) => {
                    const action = visible ? "show" : "hide";
                    Solovis.UX.Utils.emit(`@solovis/report/${action}-table`);
                  },
                },
              ],
              isShared: pageViewModel.controlHost.currentControl.featuredLinkType === FeaturedLinkType.Shared,
              isFavorite: this.isFavorite,
              featuredLinkCode: this.featuredLinkCode,
              defaultShowContent: !this._uiOptions.collapseConfigSection
            };
            const dashboardOverrides = {
              tabs: null,
              onFavorite: null,
              onShare: null,
              defaultShowContent: true,
              isDashboard: true,
            };
            const panelProps = Solovis.UX.Utils.isDashboard() ? { ...reportOptions, ...dashboardOverrides } : reportOptions;

            Solovis.UX.Utils.placeComponent('Panel', target, panelProps);
          } else {
            if (self.widgetCode) {
              this.container.addClass("paux");
              const target = self._configControlDiv[0];
              Solovis.UX.Utils.placeComponent('DashboardWidget', target, {
                id: self.widgetCode,
                title: self.widgetTitle,
                reportLink: self._getLinkToReport(),
              });
            } else {
              Solovis.UX.Utils.until(() => Boolean($(`#${configContainerId}`).length)).then(() =>
                this._setupConfigControls(reportId, configContainerId, 'modern')
              )
            }
          }
        }
        else {
          this._showReportOptionsCallback = function () {
            self._showConfigSectionInPopup();
          };
        }

        if (!this._uiOptions.hideReportOptionsButton) {
          if (this.isTopLevelReportControl()) {
            var actionBarRegion = Solovis.Site.ActionBar.getOrCreateRegion('ReportControl');
            actionBarRegion.setHtml(showConfigLink);
          }

          // Add drag handler for report settings (allows report to be dragged to Custom Panels/etc with its current state).
          Solovis.Site.DragDropManager.setupSolovisReportControlDrag(showConfigLink, self);
        }
      }


      $(this._parameterSet).bind("allParametersReady", function () {
        try {
          if (ReportControl._log) {
            console.log("ReportControl: allParametersReady");
          }

          self._loadStatus.allParametersInitialized = true;
          self._checkForAutoRun();

          // NOTE: Changing parameters can change the space available for the control, we attempt to resize the control
          //   and trigger a window resize, so that our charts will resize themselves as appropriate. We wouldn't need
          //   this workaround if there were a reliable way to get a DOM event for a DOM element changing size.
          if (self._autoSizeSpaceAvailable) {
            if (self.isTopLevelReportControl()) {
              self._updateSpaceAvailable();
              $(window).trigger("resize");
            }
          }
        }
        catch (ex) {
          Solovis.log(ex);
        }
      });

      $(this._parameterSet).bind("parameterValueChanged", function () {
        try {
          if (ReportControl._log) {
            console.log("ReportControl: parameterValueChanged");
          }

          self._checkForAutoRunOnParameterValueChange();

          // We also need to check if any of the parameters need to be persisted.                
          $(self._parameterSet.parameters).each(function (i, param: any) {
            if (param.preserveValue) {
              var paramValue = self._parameterSet.getParameterValue(param.name);
              self._setPreservedParameterValue(param.name, paramValue, param.preserveValue.from, param.preserveValue.scope);
            }
          });

          // NOTE: Changing parameters can change the space available for the control, we attempt to resize the control
          //   and trigger a window resize, so that our charts will resize themselves as appropriate. We wouldn't need
          //   this workaround if there were a reliable way to get a DOM event for a DOM element changing size.
          if (self._autoSizeSpaceAvailable) {
            if (self.isTopLevelReportControl()) {
              self._updateSpaceAvailable();
              $(window).trigger("resize");
            }
          }
        }
        catch (ex) {
          Solovis.log(ex);
        }
      });

      if (this._parameterSet.areAllParametersReady) {
        // There are no parameters to initialized, so they are already all loaded.
        this._loadStatus.allParametersInitialized = true;

        if (ReportControl._log) {
          console.log('ReportControl: allParametersReady by reason of no initialization needed');
        }
      }

      // Print mode indicator.
      if (this.isTopLevelReportControl()) {
        this._printModeDiv = $("<div>").addClass("print-mode-indicator no-print").css("display", "none").appendTo(this.container);
        this._printModeDiv.text("Print Mode");
        if (this.printMode) {
          this._printModeDiv.css("display", "block");
        }
      }

      // Error div.
      this.errorDiv = $("<div>").addClass("no-print").addClass("report-control-error-section").css("display", "none").appendTo(this.container);

      if (this._cachePolicy && this._cachePolicy.includeCacheSummaryHeader) {
        this._cacheSummaryHeaderDiv = $("<div>").addClass("no-print").addClass("report-control-cache-summary").css("display", "none").appendTo(this.container);
      }

      this._dataPackSummaryHeaderDiv = $("<div>").addClass("no-print").addClass("report-control-pack-summary").css("display", "none").appendTo(this.container);

      // NOTE: this looks like where the extra parts are added and the div which contains the chart. how do we target the container we need 
      this._partsContainer = $("<div>").appendTo(this.container);
      if (this._uiOptions.contentHeight) {
        this._partsContainer.height(this._uiOptions.contentHeight);
      }

      // Create report parts.
      if (this.report.root) {
        this._rootReportPart = this._createPartContainer({ parentElement: this._partsContainer, partDefinition: this.report.root, isRoot: true });
      }

      this._loadStatus.partContainersCreated = true;

      // Setup parameter sync manager.            
      this._parameterSyncManager = new Solovis.Reports.ReportControlHelpers.SubReportParameterSyncManager({
        reportControl: this
      });

      this._checkForAutoRun();

      this._updateSpaceAvailable();
    }

    public _getLinkToReport() {
      var isTopLevel = this.isTopLevelReportControl();

      if (!isTopLevel) {
        if (this.reportCode || this.reportAlias) {
          var reportLinkOptions: any = {
            parameters: this.getSerializedParameterValues(),
            autoRun: true,
          };
          if (this.reportCode) {
            reportLinkOptions.reportCode = this.reportCode;
          } else if (this.reportAlias) {
            reportLinkOptions.reportAlias = this.reportAlias;
          }
          var internalReportLink =
            Solovis.Reports.ReportLinker.getReportLink(reportLinkOptions);

          return internalReportLink;
        }
      }
      return null;
    }

    public _setupConfigControls(reportId: string, containerId: string, renderMode: Solovis.UI.BaseControlOptions['renderMode']) {
      const configControlContent = $(`#${containerId}`);
      if (!configControlContent.length) return;
      configControlContent.empty();

      var self = this;
      if (this._reportConfigSectionClass) {
        configControlContent.addClass(this._reportConfigSectionClass);
      }

      this._configControlHost = new Solovis.UI.ControlHost({ container: configControlContent });
      this._configControl = new Solovis.Reports.ReportConfigControl({
        reportControl: this,
        uiOptions: this._uiOptions,
        renderMode: renderMode
      });
      this._configControlHost.setCurrentControl(this._configControl);

      this._showReportOptionsCallback = function () {
        self._configControlDiv.toggle();
      };

      // NOTE: This was removed as we were having problems duplicating the initial conditions which
      // required this. The initial rationale was:
      // We run into cases where some reports being run, namely featured linked reports, are run in 
      // a different order. For those cases, they are being fired off at the same time independently 
      // and creating two config containers. There is no way to check during runtime and this removes
      // the secondary report config container.
      // $(`.sub-report-container #${reportId}`).remove();
    }

    public _checkForAutoRun() {
      // If autoRun is set, we should automatically run report once all parameters are initialized.
      // We have to wait for initialization of control because some parameter editors like those for Entity
      // or Investment need to load additional data.
      if (this._loadStatus.shouldAutoRun) {
        if (this._loadStatus.allParametersInitialized && this._loadStatus.partContainersCreated) {
          this._loadStatus.shouldAutoRun = false;

          if (!this._loadStatus.autoRunCondition || this._loadStatus.autoRunCondition.call(this)) {
            this._runReport(true);
          }
        }
      }
    }

    public _checkForAutoRunOnParameterValueChange() {
      if (this.shouldAutoRunOnParameterChange) {
        if (!this._loadStatus.autoRunCondition || this._loadStatus.autoRunCondition.call(this)) {
          this.runReport();
        }
      }
    }

    public getSerializedParameterValues() {
      if (this._parameterSet) {
        return this._parameterSet.getSerializedParameterValues();
      }
      else {
        var initialParameters = this._getParametersToUse();
        return initialParameters;
      }
    }


    /**
     * runReport is a public function that will run the report with a debounce delay.
     */
    public runReport(runReportOptions?: RunReportOptions) {
      this._runReportOptions = runReportOptions;

      var self = this;
      if (!self._debouncedRegularRunner) {
        self._debouncedRegularRunner = _.debounce(
          function () {
            if (self._loadStatus.allParametersInitialized && self._loadStatus.partContainersCreated) {
              self._runReport(false);
            }
          },
          300);
      }

      this._debouncedRegularRunner();
    };


    public getActiveRunReportOptions(): RunReportOptions {
      return this._runReportOptions;
    }


    public _validateParameters = function (parameters) {
      var self = this;
      var paramStates = [];

      $(this.report.parameters).each(function (i, param: any) {
        if (param.validation) {
          var validation = param.validation;
          var paramValue = parameters[param.name];

          var errorMessage = null;

          if (validation.required) {
            // For a list: true parameter, Solovis.isNull alone would miss a value that has been
            // emptied out (e.g. every item removed from a multi-valued picker) rather than left
            // unset -- that reaches a param.list consumer as [], not null, and was silently
            // treated as present (SE-1785).
            var isEmptyRequiredList = param.list && Array.isArray(paramValue) && paramValue.length === 0;
            if (Solovis.isNull(paramValue) || isEmptyRequiredList) {
              errorMessage = "\"" + param.label + "\" is required.";
            }
          }

          if (errorMessage) {
            var paramState = {
              name: param.name,
              message: errorMessage
            };

            paramStates.push(paramState);
          }
        }
      });

      if (this.report.parameterValidation) {
        if (this.report.parameterValidation.custom) {
          try {
            var validationResult = self.report.parameterValidation.custom.call(self._parameterSet);
            if (validationResult) {
              $(validationResult.errors).each(function (i, error) {
                paramStates.push(error);
              });
            }
          }
          catch (ex) {
            console.log(ex);
            paramStates.push({ message: ex.toString() });
          }
        }
      }

      if (paramStates.length > 0) {
        var paramValidationState = { hasErrors: true, byParameter: paramStates };
        return paramValidationState;
      }
      else {
        return null;
      }
    };

    public _runReport(wasAutoRun?: boolean) {
      var self = this;

      if (ReportControl._log) {
        console.log("ReportControl._runReport: starting report run");
      }

      if (wasAutoRun) {
        this._runReportOptions = this._reportOptions.autoRunReportOptions;
      }

      this._runCount++;
      this._lastRunStartTime = new Date().getTime();

      var runCountToUse = this._runCount;

      this._waitManager.waitingHasBegun();

      var parameters = this._parameterSet.getParameterValues();

      var serializedParameterValues = this._parameterSet.getSerializedParameterValues();
      $(this).trigger("reportRunStarted", { parameters: serializedParameterValues });

      this._lastParameterValidationState = null;
      var parameterValidationState = this._validateParameters(parameters);
      if (parameterValidationState && parameterValidationState.hasErrors) {
        this._handleReportDoneLoadingWithParameterErrors(parameterValidationState);
        return;
      }

      for (var i = 0; i < this.reportPartList.length; i++) {
        var reportPart = this.reportPartList[i];

        var parametersForControl;
        if (reportPart.partDefinition.parameterMap) {
          parametersForControl = reportPart.partDefinition.parameterMap(parameters);
        }
        else {
          parametersForControl = parameters;
        }

        if (reportPart.partControlHost && reportPart.partControlHost.currentControl) {
          var partControl = reportPart.partControlHost.currentControl;

          if (partControl.setPrintMode) {
            // Be sure to keep child report parts in sync with report print mode.
            partControl.setPrintMode(self.printMode);
          }

          // The part control does not need to worry about getting this event after it has been destroyed.
          if (partControl.state < Solovis.UI.BaseControl.ControlState.beforeDestroy) {
            if (partControl.updateParameters) {
              partControl.updateParameters(parametersForControl);
            }
          }
        }
        else if (reportPart.layoutPart) {
          if (reportPart.layoutPart.updateParameters) {
            reportPart.layoutPart.updateParameters(parametersForControl);
          }
        }

        if (reportPart.dataSources) {
          reportPart.dataSources.loadData(parametersForControl, runCountToUse);
        }
        else {
          if (reportPart.partControlHost) {
            var partControl = reportPart.partControlHost.currentControl;
            // The part control does not need to worry about getting this event after it has been destroyed.
            if (partControl.state < Solovis.UI.BaseControl.ControlState.beforeDestroy) {

              if (partControl.updateDataSets) {
                // Pass in empty data set collection (which allows controls to have predicatable sequence of calls even
                // if they don't specify data sets to include).
                partControl.updateDataSets({});
              }
            }
          }
        }
      }

      // This check will catch the case where we have no data sources in report.
      this.callAfterRender(() => {
        if (this.areAllDataSetsLoaded()) {
          this._handleReportDoneLoading();
        }
      })

      // Notify server so we know the report instance was run.
      if (this.reportCode) {
        let topLevelReportCode: string = null;
        if (this.parentReportControl) {
          let topLevelControl = this.getTopLevelReportControl();
          if (topLevelControl && topLevelControl.reportCode) {
            topLevelReportCode = topLevelControl.reportCode;
          }
        }

        this.portalServices().rpc().invoke(
          "~/ReportData/NotifyReportRun",
          {
            reportCode: this.reportCode,
            topLevelReportCode: topLevelReportCode,
            wasAutoRun: wasAutoRun
          },
          function () {
          });
      }
    }


    public _createPartContainer(options) {
      var self = this;

      var parentElement = options.parentElement;
      var partDefinition = options.partDefinition;
      var parentLayoutPart = options.parentLayoutPart ? options.parentLayoutPart : null;
      var isRoot = options.isRoot ? true : false;

      if (partDefinition.partControl) {
        var partControlType;
        if (typeof partDefinition.partControl == "string") {
          partControlType = Solovis.resolveObject(partDefinition.partControl, window);

          if (!partControlType) {
            throw "Part control type not found: " + partDefinition.partControl;
          }
        }
        else {
          partControlType = partDefinition.partControl;
        }

        var partSettings = {};
        if (partDefinition.settings) {
          partSettings = Solovis.deepCopy(partDefinition.settings);
        }

        var partControlContainer = $("<div>").appendTo(parentElement);
        if (partDefinition.style) {
          this._applyStyleToPartContainer(partDefinition.style, partControlContainer);
        }
        if (this._autoSizeSpaceAvailable && isRoot) {
          // Allow contained classes to auto-size with the parts container.               
          partControlContainer.css("height", "100%");
        }

        var partControlHost = new Solovis.UI.ControlHost({ container: partControlContainer });

        var partControlOptions: any = {
          controlHost: partControlHost,
          requiredModules: partDefinition.requiredModules ? partDefinition.requiredModules : null,
          settings: partSettings,
          visible: partDefinition.visible ? partDefinition.visible : null,
          renderMode: self.options.renderMode,
          reportControl: self,
          partElementName: partDefinition.elementName ? partDefinition.elementName : null,
          parameterOverrides: partDefinition.parameterOverrides ? partDefinition.parameterOverrides : null
        };

        if (self._persistedSettings) {
          if (partDefinition.elementName) {
            if (self._persistedSettings[partDefinition.elementName]) {
              partControlOptions.persistedSettings = self._persistedSettings[partDefinition.elementName];
            }
          }
        }

        var partControl = new partControlType(partControlOptions);
        if (isRoot) {
          // This is a root-level element, so we should tell it that the parent is visible.
          partControl.updateParentVisibility(true);
        }

        partControl.setAsCurrentControl();

        let reportPart = {
          partControlHost: partControlHost,
          partDefinition: partDefinition
        };

        this._addReportPart(reportPart);

        return reportPart;
      }
      else if (partDefinition.layout) {
        var layout = partDefinition.layout;
        var layoutPart = null;

        const layoutPartOptions = {
          partDefinition: partDefinition,
          parentLayoutPart: parentLayoutPart,
          renderMode: self.options.renderMode,
          reportControl: this
        }

        if (layout == "Vertical") {
          layoutPart = new Solovis.Reports.Layouts.VerticalLayoutPart(layoutPartOptions);

          layoutPart.createPart(parentElement);
        }
        else if (layout == "Horizontal") {
          layoutPart = new Solovis.Reports.Layouts.HorizontalLayoutPart(layoutPartOptions);

          layoutPart.createPart(parentElement);
        }
        else if (layout == "BlockWithHeader") {
          layoutPart = new Solovis.Reports.Layouts.BlockWithHeaderLayoutPart(layoutPartOptions);

          layoutPart.createPart(parentElement);
        }
        else if (layout == "Grid") {
          layoutPart = new Solovis.Reports.Layouts.GridLayoutPart(layoutPartOptions);

          layoutPart.createPart(parentElement);
        }
        else {
          throw "Unknown layout: " + layout;
        }

        if (this._autoSizeSpaceAvailable && isRoot) {
          // Allow contained classes to auto-size with the parts container.    
          if (layoutPart.layoutDiv) {
            layoutPart.layoutDiv.css("height", "100%");
          }
        }

        let reportPart = {
          partDefinition: partDefinition,
          layoutPart: layoutPart
        };

        this._addReportPart(reportPart);

        return reportPart;
      }
      else if (partDefinition.html) {
        var dynamicPartDefinition = {
          partControl: "Solovis.Reports.Parts.HtmlControl",
          settings: {
            html: partDefinition.html
          }
        };

        return this._createPartContainer({
          parentElement: parentElement,
          partDefinition: dynamicPartDefinition,
          parentLayoutPart: parentLayoutPart
        });
      }
      else {
        throw "Invalid report part.";
      }
    }


    public _applyStyleToPartContainer(style, partControlContainer) {
      // NOTE: We intentionally do not allow arbitrary styles and want to "whitelist" the ones to allow.

      if (style) {
        var boxStyle = style;
        if (boxStyle.standardBorder) {
          partControlContainer.addClass("report-standard-block-border");
        }

        if (boxStyle.maxWidth) {
          partControlContainer.css("max-width", boxStyle.maxWidth);
        }
        if (boxStyle.minWidth) {
          partControlContainer.css("min-width", boxStyle.minWidth);
        }
        if (boxStyle.width) {
          partControlContainer.css("width", boxStyle.width);
        }

        if (boxStyle.maxHeight) {
          partControlContainer.css("max-height", boxStyle.maxHeight);
        }
        if (boxStyle.minHeight) {
          partControlContainer.css("min-height", boxStyle.minHeight);
        }
        if (boxStyle.height) {
          partControlContainer.css("height", boxStyle.height);
        }

        if (Solovis.isNotNull(boxStyle.marginTop)) {
          partControlContainer.css("margin-top", boxStyle.marginTop);
        }
        if (Solovis.isNotNull(boxStyle.marginRight)) {
          partControlContainer.css("margin-right", boxStyle.marginRight);
        }
        if (Solovis.isNotNull(boxStyle.marginBottom)) {
          partControlContainer.css("margin-bottom", boxStyle.marginBottom);
        }
        if (Solovis.isNotNull(boxStyle.marginLeft)) {
          partControlContainer.css("margin-left", boxStyle.marginLeft);
        }

        if (Solovis.isNotNull(boxStyle.paddingTop)) {
          partControlContainer.css("padding-top", boxStyle.paddingTop);
        }
        if (Solovis.isNotNull(boxStyle.paddingRight)) {
          partControlContainer.css("padding-right", boxStyle.paddingRight);
        }
        if (Solovis.isNotNull(boxStyle.paddingBottom)) {
          partControlContainer.css("padding-bottom", boxStyle.paddingBottom);
        }
        if (Solovis.isNotNull(boxStyle.paddingLeft)) {
          partControlContainer.css("padding-left", boxStyle.paddingLeft);
        }

        if (Solovis.isNotNull(boxStyle.display)) {
          partControlContainer.css("display", boxStyle.display);
        }

        if (Solovis.isNotNull(boxStyle.float)) {
          partControlContainer.css("float", boxStyle.float);
        }

        if (Solovis.isNotNull(boxStyle.overflow)) {
          partControlContainer.css("overflow", boxStyle.overflow);
        }

        if (Solovis.isNotNull(boxStyle.backgroundColor)) {
          partControlContainer.css("background-color", boxStyle.backgroundColor);
        }
      }
    }


    public _addReportPart(reportPart) {
      var self = this;

      var partDefinition = reportPart.partDefinition;

      if (partDefinition.dataSources && partDefinition.dataSources.length > 0) {
        var dataSources = new Solovis.Reports.DataSourceCollection({ dataSources: partDefinition.dataSources, reportControl: self });

        $(dataSources).bind("allDataSetsLoaded", function () {
          try {
            if (ReportControl._log) {
              console.log("ReportControl: All data sets loaded for report part:")
              console.log(reportPart);
            }

            if (reportPart.partControlHost) {
              var partControl = reportPart.partControlHost.currentControl;
              if (partControl) {
                // The part control does not need to worry about getting this event after it has been destroyed.
                if (partControl.state < Solovis.UI.BaseControl.ControlState.beforeDestroy) {
                  if (partControl.updateDataSets) {
                    partControl.updateDataSets(dataSources.getDataSets());
                  }
                }
              }
            }
            else if (reportPart.layoutPart) {
              if (reportPart.layoutPart.updateDataSets) {
                reportPart.layoutPart.updateDataSets(dataSources.getDataSets());
              }
            }
          }
          catch (ex) {
            console.log(ex);
          }
          finally {
            self.callAfterRender(() => {
              if (self.areAllDataSetsLoaded()) {
                self._handleReportDoneLoading();
              }
            })
          }
        });

        $(dataSources).bind("singleDataSetLoaded", (function (event, data) {
          try {
            if (ReportControl._log) {
              console.log("ReportControl: singleDataSetLoaded, elementName: " + partDefinition.elementName + ", dataSourceName: " + data.dataSourceName);
            }

            if (partDefinition.elementName) {
              self._notifyElementDataSetLoaded(partDefinition.elementName, data.dataSourceName, data.loadCount);
            }
          }
          catch (ex) {
            console.log(ex);
          }
        }) as any);

        reportPart.dataSources = dataSources;
      }

      this.reportPartList.push(reportPart);

      if (partDefinition.elementName) {
        this.elementNameToReportPartMap[partDefinition.elementName] = reportPart;
      }
    }

    public _notifyElementDataSetLoaded(elementName, dataSourceName, loadCount) {
      // We have a single event on ReportControl for notifying on the load of any data set to facilitate
      // loading of data set collections that reference data sets on other elements.

      if (elementName && dataSourceName) {
        $(this).trigger("elementDataSetLoaded", { elementName: elementName, dataSourceName: dataSourceName, loadCount: loadCount });
      }

      this._updateErrorDiv();
    }

    public _getDataSourceHandleForElement(elementName, dataSourceName) {
      if (this.elementNameToReportPartMap[elementName]) {
        var reportPart = this.elementNameToReportPartMap[elementName];

        if (reportPart.dataSources) {
          var dataSources = reportPart.dataSources;

          var handle = dataSources._getDataSourceHandle(dataSourceName);
          return handle;
        }
      }

      return null;
    }

    public _checkIfAllDataSetsLoaded() {
      // reportPartList is not finalized until render is complete,
      // therefore we cannot be certain reports are loaded until render is complete
      if (this.state <= Solovis.UI.BaseControl.ControlState.beforeRender) {
        return false;
      }

      var allLoaded = true;
      for (var i = 0; i < this.reportPartList.length; i++) {
        var reportPart = this.reportPartList[i];
        if (reportPart.dataSources) {
          if (!reportPart.dataSources.areAllDataSetsLoaded) {
            allLoaded = false;
            break;
          }
        }
      }

      return allLoaded;
    }

    /**
     * Returns true if all data sets have been loaded.
     */
    public areAllDataSetsLoaded() {
      return this._checkIfAllDataSetsLoaded();
    }


    /**
     * Returns information about currently loaded data sets.
     */
    public getCurrentSourceDataSummary() {
      let byPart = [];
      let areAllDataSetsLoaded = true;
      let oldestCreatedAt = null;
      let includesCachedData = false;

      let packIdsSeenSet = {};
      let packIds = [];

      $(this.reportPartList).each(function (i, part: any) {
        let partInfo: any = {};

        if (part.partDefinition.elementName) {
          partInfo.elementName = part.partDefinition.elementName;
        }

        if (part.dataSources) {
          partInfo.areAllDataSetsLoaded = part.dataSources.areAllDataSetsLoaded ? true : false;
          if (!partInfo.areAllDataSetsLoaded) {
            areAllDataSetsLoaded = false;
          }

          let waitingOnList = [];

          $(part.dataSources.dataSourceHandles).each(function (handleIndex, handle: any) {
            if (handle.cacheInfo) {
              includesCachedData = true;

              if (handle.cacheInfo.createdAt) {
                let handleCreatedAt = handle.cacheInfo.createdAt;
                if (oldestCreatedAt === null) {
                  oldestCreatedAt = handleCreatedAt;
                }
                else if (handleCreatedAt < oldestCreatedAt) {
                  oldestCreatedAt = handleCreatedAt;
                }
              }
            }

            if (handle.packId) {
              if (!packIdsSeenSet[handle.packId]) {
                packIdsSeenSet[handle.packId] = true;
                packIds.push(handle.packId);
              }
            }

            if (!handle.loaded) {
              waitingOnList.push(handle.dataSource.name);
            }
          });

          if (!_.isEmpty(waitingOnList)) {
            partInfo.waitingOn = waitingOnList;
          }
        }

        byPart.push(partInfo);
      });

      let dataSummary: any = {};
      dataSummary.byPart = byPart;
      dataSummary.oldestCreatedAt = oldestCreatedAt;
      dataSummary.includesCachedData = includesCachedData;
      dataSummary.areAllDataSetsLoaded = areAllDataSetsLoaded;
      dataSummary.packIds = packIds;

      return dataSummary;
    }


    public _updateErrorDiv() {
      var self = this;

      this.errorDiv.html("");

      var hasErrors = false;

      if (this._lastParameterValidationState) {
        hasErrors = true;

        $(this._lastParameterValidationState.byParameter).each(function (i, paramState: any) {
          if (paramState.message) {
            $("<div>").text(paramState.message).appendTo(self.errorDiv);
          }
        });
      }

      var dataSourceErrorsDiv = $("<div>");
      $("<h3>").css("border", "0").text("Data Source Errors").appendTo(dataSourceErrorsDiv);

      var errorCount = 0;

      for (var i = 0; i < this.reportPartList.length; i++) {
        var reportPart = this.reportPartList[i];
        if (reportPart.dataSources) {
          $(reportPart.dataSources.dataSourceHandles).each(function (handleIndex, handle: any) {
            if (handle.loaded && handle.error) {
              if (!handle.errorInSourceRef) {
                errorCount++;

                if (handle.error.message) {
                  $("<div>").text(handle.error.message).appendTo(dataSourceErrorsDiv);
                }
              }
            }
          });
        }
      }

      if (hasErrors || errorCount > 0) {
        this.errorDiv.css("display", "block");

        if (errorCount > 0) {
          this.errorDiv.append(dataSourceErrorsDiv);
        }
      }
      else {
        this.errorDiv.css("display", "none");
      }
    }


    public _handleReportDoneLoadingWithParameterErrors(parameterValidationState) {
      this._lastParameterValidationState = parameterValidationState;

      this._handleReportDoneLoading();
    }

    public _handleReportDoneLoading() {
      this._waitManager.waitingHasEnded();

      // All report data has been loaded at this point.
      $(this).trigger("reportDataLoaded");

      if (this._lastRunStartTime) {
        let lastRunStartTime = this._lastRunStartTime;
        let dataLoadedTime = new Date().getTime();

        let dataLoadElapsedMs = dataLoadedTime - lastRunStartTime;

        this._lastDataLoadElapsedMs = dataLoadElapsedMs;

        if (ReportControl._log) {
          console.log("ReportControl: Data finished loading in (milliseconds): " + dataLoadElapsedMs);
        }

        this._lastRunStartTime = null;
      }

      this._updateErrorDiv();

      let dataSummary = this.getCurrentSourceDataSummary();

      if (this._cacheSummaryHeaderDiv) {
        // The cache policy indicated we need to notify user of cached data in the report.                
        if (dataSummary.includesCachedData) {
          var cacheSummaryDiv = $("<div>");
          cacheSummaryDiv.append(document.createTextNode("Includes cached data"));
          if (dataSummary.oldestCreatedAt) {
            var oldestCacheDate = new Date(dataSummary.oldestCreatedAt);
            var oldestCacheDateStr = kendo.toString(oldestCacheDate, "MMM d, yyyy h:mm:ss tt");
            cacheSummaryDiv.append(document.createTextNode(" from " + oldestCacheDateStr));
          }
          cacheSummaryDiv.append(document.createTextNode("."));

          this._cacheSummaryHeaderDiv.html(cacheSummaryDiv as any);
          this._cacheSummaryHeaderDiv.toggle(true);
        }
        else {
          this._cacheSummaryHeaderDiv.toggle(false);
        }
      }

      if (!_.isEmpty(dataSummary.packIds)) {
        this._dataPackSummaryHeaderDiv.html("");
        let packSpan = $("<span>").text("Data pack used in processing of this report.").appendTo(this._dataPackSummaryHeaderDiv);
        packSpan.css("cursor", "pointer");

        if (dataSummary.packIds) {
          Solovis.UI.PopupLinkPanel.onElementClick(packSpan, (e) => {
            let links = [
              {
                label: "Data Pack Details",
                click: () => {
                  let detailDialog = new Solovis.Reports.DataPacks.DataPackDetailDialog({
                    packIds: dataSummary.packIds
                  });
                  detailDialog.show();
                }
              }
            ];

            Solovis.UI.PopupLinkPanel.showLinks({
              links: links,
              offset: e
            });
          });
        }

        this._dataPackSummaryHeaderDiv.toggle(true);
      }
      else {
        this._dataPackSummaryHeaderDiv.toggle(false);
      }
    }


    /**
     * Retrieve list of parameter information [ { name, label, parameterType, list } ]
     */
    public getParameterInfos() {
      var paramInfos = [];

      if (this.report && this.report.parameters) {
        $(this.report.parameters).each(function (i, param: any) {
          var paramInfo = {
            name: param.name,
            label: param.label,
            parameterType: param.parameterType,
            list: param.list ? true : false,
            parameterControl: param.parameterControl,
            settings: param.settings
          }

          paramInfos.push(paramInfo);
        });
      }

      return paramInfos;
    }


    public setParameterValues(parameters) {
      if (this._parameterSet) {
        this._parameterSet.setParameterValues(parameters);
      }
      else {
        // TODO: Have way to update initial parameter values before _parameterSet is initialized.
      }
    }

    public getParameterValues() {
      if (this._parameterSet) {
        return this._parameterSet.getParameterValues();
      }
      else {
        // TODO: Have way to get initial parameter values before _parameterSet is initialized.
      }
    }

    /**
     * This will invoke a method using the report part control for element name as the this pointer.
     * @param elementName
     * @param method
     */
    public invokeOnReportPart(elementName, method) {
      // TODO: Consider handling case where control hasn't been created yet.
      var partControl = this.getReportPartControl(elementName);
      if (partControl) {
        method.call(partControl);
      }
    }


    /**
     * This will retrieve control for a report part.
     * @param elementName
     */
    public getReportPartControl(elementName) {
      var partControl = null;

      $(this.reportPartList).each(function (i, reportPart: any) {
        var partDefinition = reportPart.partDefinition;
        if (partDefinition.elementName && partDefinition.elementName == elementName) {
          // TODO: Consider handling case where control hasn't been created yet.
          var partControlHost = reportPart.partControlHost;
          if (partControlHost && partControlHost.currentControl) {
            partControl = partControlHost.currentControl;
          }
        }
      });

      return partControl;
    }

    /**
     * Returns element names of named parts.
     */
    public getElementNameList(): string[] {
      var partElementNames = [];

      $(this.reportPartList).each(function (i, reportPart: any) {
        var partDefinition = reportPart.partDefinition;
        if (partDefinition.elementName && partDefinition.elementName) {
          partElementNames.push(partDefinition.elementName);
        }
      });

      return partElementNames;
    }

    public setPrintMode(newPrintMode) {
      this.printMode = newPrintMode;

      // Update UI indicator.
      if (this._printModeDiv) {
        if (this.printMode) {
          this._printModeDiv.css("display", "block");
        }
        else {
          this._printModeDiv.css("display", "none");
        }
      }
    }


    public isTopLevelReportControl() {
      return !this.parentReportControl;
    }

    public getTopLevelReportControl() {
      if (!this.parentReportControl) {
        return this;
      }
      else {
        return this.parentReportControl.getTopLevelReportControl();
      }
    }

    private getUserSavedName() {
      const pageViewModel = (window as any).PageViewModel;

      // For user reports always use report name unless it is saved as and loaded as a report alias
      if (!pageViewModel.reportAlias && (!pageViewModel.isSystemDefined || this.featuredLinkCode)) {
        return pageViewModel.reportName;
      }

      return null;
    }

    public getReportTitle() {
      // A data source on the first reportPart with the name of "title" will be treated as the report title.
      // A user of ReportControl should wait for "reportDataLoaded" event before retrieving the title.
      let title = null
      const titleSettings = this.titleSettings

      title = this.getUserSavedName();

      if (titleSettings?.defaultTitle) {
        title = titleSettings.defaultTitle;
      }

      if (titleSettings?.useParameter?.name) {
        const holder = this._parameterSet._paramNameToHolderMap[titleSettings.useParameter.name];
        if (holder) {
          const controlType = ParameterTypes.getParameterControlForParameterType(holder.paramInfo.parameterType);
          const control = Solovis.resolveObject(controlType);
          const newTitle = Array.isArray(holder?.value) ? holder.value.map(control.getLabel).join(", ") : control.getLabel?.(holder?.value);
          if (newTitle) {
            title = [titleSettings.useParameter.prefix, newTitle, titleSettings.useParameter.suffix].filter(part => !!part).join(" ");
          }
        } else {
          console.warn(`No parameter found for ${titleSettings.useParameter.name}`);
        }
      }

      let handle = null;
      if (titleSettings?.dynamic?.ref) {
        handle = this._getDataSourceHandleForElement(titleSettings.dynamic.ref.elementName, titleSettings.dynamic.ref.dataSourceName);
      }
      else {
        // The default dynamic title is elementName="Root" and dataSourceName="title"
        handle = this._getDataSourceHandleForElement("Root", "title");
      }

      if (handle && handle.loaded && typeof handle.dataSet == "string") {
        return handle.dataSet;
      }

      if (!title && this._reportName) {
        title = this._reportName;
      }

      return title;
    }


    public collectPersistedSettings() {
      if (this._rootReportPart) {
        var persistedSettings: any = {
        };

        if (this._runCount > 0) {
          persistedSettings.hasRun = true;
        }

        var collectSettingsForPart = function (reportPart) {
          if (reportPart) {
            if (reportPart.layoutPart) {
              reportPart.layoutPart.collectSettingsForLayoutPart(collectSettingsForPart);
            }
            else if (reportPart.partDefinition.elementName) {
              if (reportPart.partControlHost) {
                if (reportPart.partControlHost.currentControl && reportPart.partControlHost.currentControl.getPersistedSettings) {
                  persistedSettings[reportPart.partDefinition.elementName] = reportPart.partControlHost.currentControl.getPersistedSettings();
                }
              }
            }
          }
        };

        collectSettingsForPart(this._rootReportPart);

        let partHiddenOverrides = this.getPartHiddenOverrideList();
        if (!_.isEmpty(partHiddenOverrides)) {
          persistedSettings.__partHiddenOverrides = partHiddenOverrides;
        }

        return persistedSettings;
      }
      else {
        if (this._persistedSettings) {
          return Solovis.deepCopy(this._persistedSettings);
        }
        else {
          return null;
        }
      }
    }


    public _updateSpaceAvailable(width?, height?) {
      if (this.report && this.report.autoSizeSpaceAvailable) {
        var newWidth = null;
        if (Solovis.isNotNull(width)) {
          newWidth = width;
        }

        var newHeight = null;
        if (Solovis.isNotNull(height)) {
          newHeight = height;
        }
        else {
          if (this.isTopLevelReportControl()) {
            newHeight = $(window).height() - this.container.offset().top;
          }
          else {
            if (this._spaceAvailable && this._spaceAvailable.height) {
              newHeight = this._spaceAvailable.height;
            }
          }
        }

        if (Solovis.isNotNull(newWidth)) {
          if (this.container) {
            this.container.css("width", newWidth + "px");
          }
        }

        if (Solovis.isNotNull(newHeight)) {
          if (this.container) {
            this.container.css("height", newHeight + "px");

            // Determine difference in height between container and _partsContainer.
            if (this._partsContainer) {
              var partsContainerOffset = this._partsContainer.offset().top - this.container.offset().top;
              this._partsContainer.css("height", (newHeight - partsContainerOffset) + "px");
            }
          }
        }

        $(this.reportPartList).each(function (i, partEntry: any) {
          if (partEntry.partDefinition && partEntry.partDefinition.settings) {
            if (typeof (partEntry.partDefinition.settings.onReportAvailableSpaceUpdate) === "function") {
              partEntry.partDefinition.settings.onReportAvailableSpaceUpdate.call(partEntry.partControlHost.currentControl, newWidth, newHeight);
            }
          }
        });
      }
    }


    public setSpaceAvailable(width, height) {
      // A container for a report can inform the report control how much space it has available.
      if (Solovis.isNotNull(width) && Solovis.isNotNull(height)) {
        this._spaceAvailable = {
          width: width,
          height: height
        };
      }

      this._updateSpaceAvailable(width, height);
    }



    /**
     * Shows the report options menu. This is intended for external consumption so that a report block header could
     * take over responsibility for showing the options menu.
     * @param options
     */
    public showReportOptionsMenu(options) {

      var optionsElement = options.optionsElement;
      if (!optionsElement) {
        throw "options.optionsElement not specified"
      }

      this._showReportOptionsMenu(optionsElement);
    }

    public _showReportOptionsMenu(optionsElement) {
      var self = this;

      var links = [];

      var isTopLevel = this.isTopLevelReportControl();

      var showConfigSectionInline = Solovis.isNotNull(this._uiOptions.showConfigSectionInline) ?
        this._uiOptions.showConfigSectionInline
        :
        isTopLevel;

      if (!showConfigSectionInline || !this._configControlDivVisible) {
        links.push({
          label: "Show Report Options",
          click: function () {
            if (showConfigSectionInline) {
              self._showConfigControlsInline(true);
            }
            else {
              self._showConfigSectionInPopup();
            }
          }
        });
      }
      else {
        links.push({
          label: "Hide Report Options",
          click: function () {
            self._showConfigControlsInline(false);
          }
        });
      }

      if (isTopLevel) {
        if (!this.printMode) {
          links.push({
            label: "Enable Print Mode",
            click: function () {
              self.setPrintMode(true);
            }
          });
        }
        else {
          links.push({
            label: "Disable Print Mode",
            click: function () {
              self.setPrintMode(false);
            }
          });
        }
      }

      if (!isTopLevel) {
        const internalReportLink = self._getLinkToReport();
        links.push({ label: "Open In New Tab", href: internalReportLink, target: "_blank" });
      }

      if (this.report && this.report.help) {
        links.push({
          label: "Report Help",
          click: function () { self._showReportHelp(); }
        });
      }

      if (this.report && this.report.relatedLinks) {
        links.push({
          label: "Related Links",
          click: function () { self._showRelatedLinks(); }
        });
      }

      if (Solovis.Portal.CachedPermissions.doesUserHavePermission("ReportEdit")) {
        if (this.reportCode) {
          links.push({ separator: true });
          links.push({
            label: "Report Definition",
            href: "~/DataManagement/ReportEditor/ReportDefinition?code=" + encodeURIComponent(this.reportCode)
          });
        }
      }

      Solovis.UI.PopupLinkPanel.showLinks({
        offsetElement: optionsElement,
        offsetRight: true,
        offsetBottom: true,
        fromRightEdge: true,
        links: links,
      });
    }

    public _showConfigControlsInline(show) {
      if (show) {
        this._configControlDiv.toggle(true);
        this._configControlDivVisible = true;
        this._updateSpaceAvailable();
      }
      else {
        this._configControlDiv.toggle(false);
        this._configControlDivVisible = false;
        this._updateSpaceAvailable();
      }
    }

    public _showConfigSectionInPopup() {
      var self = this;

      if (this._configWindowDialog) {
        // We already have a config window open.
        return;
      }

      var configWindowDiv = $("<div>");

      var configControlDiv = $("<div>").appendTo(configWindowDiv);
      var configControlHost = new Solovis.UI.ControlHost({ container: configControlDiv });

      var desiredWidth = Math.min(0.6 * $(window).width(), 700);
      var desiredHeight = Math.min(0.8 * $(window).width(), 400);

      var buttons = [
        {
          label: "Run",
          click: function () {
            self.runReport();
          }
        }
      ];

      this._configWindowDialog = new Solovis.UI.Dialog({
        container: configWindowDiv,

        title: "Report Options",
        autoOpen: true,
        center: true,
        destroyOnClose: true,
        actions: ["Close"],
        noAnimation: true,
        width: desiredWidth,
        height: desiredHeight,
        buttons: buttons,

        activate: function () {
          var configControl = new Solovis.Reports.ReportConfigControl({
            reportControl: self,
            uiOptions: self._uiOptions,
            alwaysHideRunButton: true
          });

          configControlHost.setCurrentControl(configControl);
        },

        deactivate: function () {
          configControlHost.setCurrentControl(null);
          self._configWindowDialog = null;
        }
      });
    }


    public _showRelatedLinks() {
      var relatedLinks = (this.report && this.report.relatedLinks) ? this.report.relatedLinks.call(this) : [];

      var dialogDiv = $("<div>");

      var linkTable = $("<table>").addClass("table table-striped").appendTo(dialogDiv);
      var linkTableBody = $("<tbody>").appendTo(linkTable);

      $(relatedLinks).each(function (i, relatedLink: any) {
        var row = $("<tr>").appendTo(linkTableBody);
        var cell = $("<td>").appendTo(row);

        var link = $("<a>").text(relatedLink.label).appendTo(cell);
        if (relatedLink.href) {
          link.attr("href", relatedLink.href);
        }
      });

      var dialog = new Solovis.UI.Dialog({
        container: dialogDiv,

        title: "Related Links",
        modal: true,
        center: true,
        autoOpen: true,
        minWidth: 200,
        destroyOnClose: true,
        actions: ["Close"]
      });
    }


    public _showReportHelp() {
      Solovis.Site.HelpManager.showCustomHelpDialog(
        this.report.help,
        {
          title: "Report Help",
          resourceId: this.reportResourceId
        });
    }



    /**
     * Retrieve data context that should be submitted to server when loading data sets for this report.
     */
    public getDataContextToUse() {
      if (typeof (this._dataContext) !== "undefined") {
        return this._dataContext;
      }
      else {
        // Check all parent report controls for a specified data context.
        let parentReportControl = this.parentReportControl;
        while (parentReportControl) {
          if (typeof (parentReportControl._dataContext) !== "undefined") {
            return parentReportControl._dataContext;
          }
          parentReportControl = parentReportControl.parentReportControl;
        }
      }

      if (this.report && this.report.allowDataPacks) {
        let defaultMountCode = Solovis.Reports.DataPacks.DataPackSelector.getDefaultMountCode();
        if (defaultMountCode) {
          let dataContext = {
            perspectiveCode: defaultMountCode
          };
          return dataContext;
        }
      }
    }

    /**
     * Create a report reference object used for creating draggable references to reports or specific elements within reports.
     * @param elementName
     */
    public createReportReference(elementName) {
      var reportRef: any = {
        serverRootUrl: Solovis.resolveFullUrl("~/"),
        reportCode: this.reportCode,
        parameters: this.getSerializedParameterValues(),
        parameterInfos: this.getParameterInfos(),
        persistedSettings: this.collectPersistedSettings()
      };

      if (elementName) {
        reportRef.elementName = elementName;
      }

      let dataContext = this.getDataContextToUse();
      if (dataContext) {
        reportRef.dataContext = dataContext;
      }

      return reportRef;
    }


    public doDestroy() {
      if (this._windowResizeForAutoSizeHandler) {
        $(window).unbind("resize", this._windowResizeForAutoSizeHandler);
        this._windowResizeForAutoSizeHandler = null;
      }

      // Clear out all report part control hosts.
      for (var i = 0; i < this.reportPartList.length; i++) {
        var reportPart = this.reportPartList[i];
        if (reportPart.partControlHost) {
          reportPart.partControlHost.setCurrentControl(null);
        }
      }
    }


    public getSiteReportSettings() {
      let portalServices = this.portalServices();
      let custom = portalServices.customEnvironmentUserData();

      if (custom && custom.Reports) {
        let customReports = custom.Reports;

        if (customReports.byCode && this.reportCode) {
          let reportSettings = customReports.byCode[this.reportCode];
          return reportSettings;
        }
      }
    }


    public setPartHiddenOverride(partElementName: string, hide: boolean) {
      // A part hidden override means that a part should not be shown (regardless of its usual visibility setting).
      if (this.isPartHiddenOverrideSet(partElementName) != (!!hide)) {
        if (hide) {
          this._partHiddenOverrideMap[partElementName] = true;
        }
        else {
          delete this._partHiddenOverrideMap[partElementName];
        }

        let partControl: Solovis.Reports.Parts.PartControl = this.getReportPartControl(partElementName);
        if (partControl) {
          partControl._updateVisibility();
          partControl.onVisibilityChanged();
        }
      }
    }

    public isPartHiddenOverrideSet(partElementName: string) {
      return !!this._partHiddenOverrideMap[partElementName];
    }

    public getPartHiddenOverrideList() {
      let partHiddenOverrides = [];
      for (let partElementName in this._partHiddenOverrideMap) {
        partHiddenOverrides.push(partElementName);
      }
      return partHiddenOverrides;
    }

    public clearPartHiddenOverrides() {
      let partHiddenOverrides = this.getPartHiddenOverrideList();
      for (let partElementName of partHiddenOverrides) {
        this.setPartHiddenOverride(partElementName, false);
      }
    }

    public showPartHiddenOverrideDialog() {
      new Solovis.Reports.ReportControlHelpers.ReportControlHiddenPartOverrideDialog({ reportControl: this }).show();
    }
  }

}
